import { useQuery } from "@tanstack/react-query";
import {
  SWITCHBOARD_OUTCOMES,
  type SwitchboardFilters,
  type SwitchboardResponse,
  type SwitchboardCallRow,
} from "@t3tools/contracts";
import { RefreshCwIcon, RotateCcwIcon } from "lucide-react";
import { useState } from "react";
import {
  ChartPanel,
  DashboardFilterField,
  DashboardFilterRow,
  DashboardSelect,
  DashboardsPageFrame,
  DataTable,
  KpiCard,
  KpiGrid,
  HorizonTabs,
  dashboardControlClassName,
} from "./dashboardKit";
import { SwitchboardDailyHealth } from "./SwitchboardDailyHealth";
import {
  switchboardHealthQueryOptions,
  switchboardSnapshotQueryOptions,
} from "./switchboardReactQuery";
import {
  switchboardLabel,
  switchboardOutcomeLabel,
  switchboardReasonLabel,
  switchboardAnswerKindLabel,
  switchboardCallPathLabel,
} from "./switchboardOutcomes";
import { SwitchboardPathReview } from "./SwitchboardPathReview";
import { SwitchboardImpact, formatSavedTime } from "./SwitchboardImpact";

const buttonClass =
  "inline-flex items-center gap-1.5 rounded-md border border-border bg-background px-2.5 py-1.5 text-xs font-medium text-foreground hover:bg-accent disabled:opacity-40";
function formatTime(value: string | null, timezone: string) {
  return value
    ? new Intl.DateTimeFormat("en-US", {
        timeZone: timezone,
        dateStyle: "medium",
        timeStyle: "short",
      }).format(new Date(value))
    : "—";
}
function filterKey(filters: SwitchboardFilters) {
  return JSON.stringify(Object.entries(filters).sort(([a], [b]) => a.localeCompare(b)));
}
function Filters(props: {
  data: SwitchboardResponse | undefined;
  filters: SwitchboardFilters;
  dirty: boolean;
  details: boolean;
  onChange: (key: keyof SwitchboardFilters, value: string) => void;
  onApply: () => void;
  onReset: () => void;
}) {
  const { filters, data } = props;
  const invalidDates = Boolean(
    filters.dateStart && filters.dateEnd && filters.dateStart > filters.dateEnd,
  );
  return (
    <section className="rounded-lg border border-border/70 bg-background/70 p-4 shadow-xs">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <div>
          <h2 className="text-sm font-semibold text-foreground">Call filters</h2>
          <p className="text-xs text-muted-foreground">
            Call dates use {data?.timezone ?? "America/New_York"}. Edit filters, then apply to
            update the dashboard.{" "}
            {props.details
              ? "Recorded answer type, outcome, activities, and search refine path and call detail results."
              : "Health includes all Daily attempts in this scope, including administrative usage."}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button type="button" onClick={props.onReset} className={buttonClass}>
            <RotateCcwIcon className="size-3.5" aria-hidden />
            Reset filters
          </button>
          <button
            type="button"
            onClick={props.onApply}
            disabled={!props.dirty || invalidDates}
            className="rounded-md bg-foreground px-2.5 py-1.5 text-xs font-medium text-background hover:opacity-90 disabled:opacity-40"
          >
            Apply filters
          </button>
        </div>
      </div>
      <DashboardFilterRow>
        {(
          [
            ["dateStart", "Called from"],
            ["dateEnd", "Called through"],
          ] as const
        ).map(([key, label]) => (
          <DashboardFilterField key={key} label={label}>
            <input
              type="date"
              value={filters[key] ?? ""}
              onChange={(event) => props.onChange(key, event.target.value)}
              className={dashboardControlClassName}
            />
          </DashboardFilterField>
        ))}
        <DashboardFilterField label="Clinic">
          <DashboardSelect
            value={filters.organizationId ?? ""}
            onChange={(event) => props.onChange("organizationId", event.target.value)}
          >
            <option value="">All clinics</option>
            {data?.organizations.map((row) => (
              <option key={row.id} value={row.id}>
                {row.name}
              </option>
            ))}
          </DashboardSelect>
        </DashboardFilterField>
        <DashboardFilterField label="Navigator">
          <DashboardSelect
            value={filters.navigatorId ?? ""}
            onChange={(event) => props.onChange("navigatorId", event.target.value)}
          >
            <option value="">All navigators</option>
            {data?.navigators.map((row) => (
              <option key={row.id} value={row.id}>
                {row.name}
              </option>
            ))}
          </DashboardSelect>
        </DashboardFilterField>
        {props.details ? (
          <>
            <DashboardFilterField label="Recorded answer type">
              <DashboardSelect
                value={filters.answerKind ?? ""}
                onChange={(event) => props.onChange("answerKind", event.target.value)}
              >
                <option value="">All answer types</option>
                {["human", "voicemail", "screening", "unknown"].map((kind) => (
                  <option key={kind} value={kind}>
                    {switchboardAnswerKindLabel(kind)}
                  </option>
                ))}
              </DashboardSelect>
            </DashboardFilterField>
            <DashboardFilterField label="Outcome">
              <DashboardSelect
                value={filters.outcome ?? ""}
                onChange={(event) => props.onChange("outcome", event.target.value)}
              >
                <option value="">All outcomes</option>
                {SWITCHBOARD_OUTCOMES.map((outcome) => (
                  <option key={outcome} value={outcome}>
                    {switchboardOutcomeLabel(outcome)}
                  </option>
                ))}
              </DashboardSelect>
            </DashboardFilterField>
            <DashboardFilterField label="Activities">
              <DashboardSelect
                value={filters.activity ?? ""}
                onChange={(event) => props.onChange("activity", event.target.value)}
              >
                <option value="">All calls</option>
                <option value="linked">Has linked activities</option>
                <option value="missing">No linked activities</option>
              </DashboardSelect>
            </DashboardFilterField>
            <DashboardFilterField label="Search">
              <input
                type="search"
                value={filters.search ?? ""}
                maxLength={200}
                onChange={(event) => props.onChange("search", event.target.value)}
                className={dashboardControlClassName}
                placeholder="Carespace, call ID, or reason"
              />
            </DashboardFilterField>
          </>
        ) : null}
      </DashboardFilterRow>
      {invalidDates ? (
        <p role="alert" className="mt-3 text-xs text-red-600">
          Start date must be on or before end date.
        </p>
      ) : null}
      {props.dirty ? (
        <p className="mt-3 text-xs text-muted-foreground">You have unapplied filter changes.</p>
      ) : null}
    </section>
  );
}

function CallPathReviewCell({ row, timezone }: { row: SwitchboardCallRow; timezone: string }) {
  const pathReview = row.pathReview;
  if (!pathReview)
    return <span className="text-muted-foreground">Path review data unavailable</span>;

  const path = pathReview.reviewed;
  if (!path) return <span className="text-muted-foreground">No structured review</span>;

  const steps = [
    ["First answerer", "firstAnswerer"],
    ["Screener decision", "screenerDecision"],
    ["Handoff failure", "handoffFailureReason"],
  ] as const;
  const promptVersion = pathReview.promptVersion;

  return (
    <div className="min-w-64 space-y-1.5">
      <div className="flex flex-wrap items-center gap-2 text-xs">
        <span className="font-medium">
          {promptVersion === null ? "Prompt version unavailable" : `Prompt v${promptVersion}`}
        </span>
        {pathReview.disagrees ? (
          <span className="rounded-full bg-amber-500/10 px-2 py-0.5 text-amber-700 dark:text-amber-400">
            Review disagreement
          </span>
        ) : null}
      </div>
      {steps.map(([label, key]) => (
        <p key={key} className="text-xs">
          <span className="text-muted-foreground">{label}: </span>
          <span className="font-medium">
            {switchboardCallPathLabel(
              path[key] ?? (key === "firstAnswerer" ? "unknown" : "not_applicable"),
              key,
            )}
          </span>
          <span className="text-muted-foreground">
            {" · Recorded: "}
            {pathReview.recorded
              ? switchboardCallPathLabel(pathReview.recorded[key], key, "No recorded value")
              : "No recorded agent path"}
          </span>
        </p>
      ))}
      {pathReview.reviewedAt ? (
        <p className="text-[10px] text-muted-foreground">
          Reviewed {formatTime(pathReview.reviewedAt, timezone)}
        </p>
      ) : null}
    </div>
  );
}

export function SwitchboardCallTable({
  data,
  onPage,
  fetching,
}: {
  data: SwitchboardResponse;
  onPage: (page: number) => void;
  fetching: boolean;
}) {
  const total = data.summary.totalCalls;
  const pages = Math.max(1, Math.ceil(total / data.pageSize));
  return (
    <ChartPanel title="Switchboard calls">
      <p className="mb-3 text-xs text-muted-foreground">
        One row per dialing attempt, newest first. Includes failed preparation; undialed queue
        entries are excluded. Activity links open Carespaces.
      </p>
      <DataTable<SwitchboardCallRow>
        rows={[...data.calls]}
        rowKey={(row) => row.id}
        emptyMessage="No calls match the applied filters."
        maxHeightClass="max-h-[36rem]"
        columns={[
          {
            key: "dialedAt",
            label: "Called at",
            render: (row) => (
              <div className="whitespace-nowrap">
                {formatTime(row.dialedAt, data.timezone)}
                <p className="mt-1 text-xs text-muted-foreground">
                  {row.finishedAt
                    ? `Ended ${formatTime(row.finishedAt, data.timezone)}`
                    : "In progress"}
                </p>
              </div>
            ),
          },
          {
            key: "carespaceName",
            label: "Carespace",
            render: (row) => (
              <a
                className="font-medium text-sky-700 underline underline-offset-2 dark:text-sky-400"
                href={`https://app.craniometrix.com/carespaces/${encodeURIComponent(row.carespaceId)}`}
                target="_blank"
                rel="noreferrer"
              >
                {row.carespaceName}
              </a>
            ),
          },
          { key: "organizationName", label: "Clinic" },
          { key: "navigatorName", label: "Navigator" },
          {
            key: "provider",
            label: "Provider / call ID",
            render: (row) => (
              <div className="min-w-36">
                {switchboardLabel(row.provider)}
                <p
                  title={row.providerCallId ?? undefined}
                  className="mt-1 max-w-40 truncate font-mono text-xs text-muted-foreground"
                >
                  {row.providerCallId ?? "Provider call not created"}
                </p>
              </div>
            ),
          },
          {
            key: "answerKind",
            label: "Recorded answer type",
            render: (row) => <span>{switchboardAnswerKindLabel(row.answerKind)}</span>,
          },
          {
            key: "pathReview",
            label: "Reviewed call path",
            render: (row) => <CallPathReviewCell row={row} timezone={data.timezone} />,
          },
          {
            key: "voicemailDelivered",
            label: "Voicemail message",
            render: (row) =>
              row.voicemailDelivered
                ? "Complete message recorded"
                : row.answerKind === "voicemail"
                  ? "Complete message not recorded"
                  : "—",
          },
          {
            key: "outcome",
            label: "Saved outcome",
            render: (row) => (
              <span className="font-medium">{switchboardOutcomeLabel(row.outcome)}</span>
            ),
          },
          {
            key: "outcomeReason",
            label: "Reason",
            render: (row) => (
              <span title={row.outcomeReason ?? undefined}>
                {switchboardReasonLabel(row.outcomeReason, row.outcome)}
              </span>
            ),
          },
          {
            key: "durationSeconds",
            label: "Connected time",
            align: "right",
            render: (row) => (
              <span className="whitespace-nowrap">
                {row.durationSeconds === null
                  ? "—"
                  : `${Math.floor(row.durationSeconds / 60)}m ${Math.floor(row.durationSeconds % 60)}s`}
              </span>
            ),
          },
          {
            key: "timeSavedSeconds",
            label: "Est. CN time saved",
            render: (row) =>
              row.timeSavedSeconds === null ? "—" : formatSavedTime(row.timeSavedSeconds),
          },
          {
            key: "callback",
            label: "Callback",
            render: (row) => row.callback ?? "—",
          },
          {
            key: "activities",
            label: "Activities",
            render: (row) =>
              row.activities.length ? (
                <div className="grid min-w-56 gap-2">
                  {row.activities.map((activity) => (
                    <a
                      key={activity.id}
                      className="text-sky-700 underline underline-offset-2 dark:text-sky-400"
                      href={`https://app.craniometrix.com/carespaces/${encodeURIComponent(row.carespaceId)}/activities/${encodeURIComponent(activity.id)}`}
                      target="_blank"
                      rel="noreferrer"
                    >
                      {activity.title}{" "}
                      <span className="ml-1 text-xs text-muted-foreground">
                        ({switchboardLabel(activity.type)})
                      </span>
                    </a>
                  ))}
                </div>
              ) : (
                <span className="text-muted-foreground">No linked activity</span>
              ),
          },
        ]}
      />
      <div className="mt-3 flex flex-wrap items-center justify-between gap-3 text-xs text-muted-foreground">
        <span>
          {total
            ? `${Math.min((data.page - 1) * data.pageSize + 1, total)}–${Math.min(data.page * data.pageSize, total)} of ${total.toLocaleString()} calls`
            : "0 calls"}
        </span>
        <div className="flex items-center gap-3">
          <button
            className={buttonClass}
            type="button"
            disabled={fetching || data.page <= 1}
            onClick={() => onPage(data.page - 1)}
          >
            Previous
          </button>
          <span>
            Page {data.page} of {pages}
          </span>
          <button
            className={buttonClass}
            type="button"
            disabled={fetching || data.page >= pages}
            onClick={() => onPage(data.page + 1)}
          >
            Next
          </button>
        </div>
      </div>
    </ChartPanel>
  );
}

export function SwitchboardDashboard() {
  const [draftFilters, setDraftFilters] = useState<SwitchboardFilters>({ provider: "daily" });
  const [appliedFilters, setAppliedFilters] = useState<SwitchboardFilters>({ provider: "daily" });
  const [page, setPage] = useState(1);
  const [tab, setTab] = useState("impact");
  const [healthPage, setHealthPage] = useState(1);
  const query = useQuery({
    ...switchboardSnapshotQueryOptions(appliedFilters, page),
    enabled: tab === "impact",
  });
  const healthQuery = useQuery({
    ...switchboardHealthQueryOptions(appliedFilters, healthPage),
    enabled: tab === "health",
  });
  const activeQuery = tab === "impact" ? query : healthQuery;
  // Keep facet options available while changing filters/pages, without showing stale metrics.
  const [options, setOptions] = useState<SwitchboardResponse | undefined>();
  if (query.data && options !== query.data) setOptions(query.data);
  const data = query.data;
  const updateFilter = (key: keyof SwitchboardFilters, value: string) =>
    setDraftFilters((current) => {
      const next = { ...current };
      if (value) next[key] = value;
      else delete next[key];
      return next;
    });
  return (
    <DashboardsPageFrame className="space-y-5">
      <header className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <p className="text-xs font-semibold tracking-wide text-muted-foreground uppercase">
            Product
          </p>
          <h1 className="text-2xl font-semibold tracking-tight text-foreground">Switchboard</h1>
          <p className="mt-1 max-w-3xl text-sm leading-relaxed text-muted-foreground">
            See time saved, connected care, adoption, and reviewed paths for Daily Switchboard.
          </p>
        </div>
        <div className="flex flex-col items-end gap-2 text-right">
          <button
            className={buttonClass}
            type="button"
            onClick={() => void activeQuery.refetch()}
            disabled={activeQuery.isFetching}
          >
            <RefreshCwIcon
              className={activeQuery.isFetching ? "size-3.5 animate-spin" : "size-3.5"}
              aria-hidden
            />
            {activeQuery.isFetching ? "Refreshing…" : "Refresh"}
          </button>
          <p className="text-xs text-muted-foreground">
            {activeQuery.data
              ? `Refreshed ${formatTime(activeQuery.data.generatedAt, activeQuery.data.timezone)}`
              : "Live call query"}
          </p>
        </div>
      </header>
      <HorizonTabs
        idPrefix="switchboard"
        value={tab}
        onChange={setTab}
        options={[
          { value: "impact", label: "Impact & adoption" },
          { value: "health", label: "Daily health" },
        ]}
      />
      {tab === "impact" && data ? <SwitchboardImpact data={data} /> : null}
      <Filters
        data={options}
        details={tab === "impact"}
        filters={draftFilters}
        dirty={filterKey(draftFilters) !== filterKey(appliedFilters)}
        onChange={updateFilter}
        onApply={() => {
          setAppliedFilters({ ...draftFilters });
          setPage(1);
          setHealthPage(1);
        }}
        onReset={() => {
          setDraftFilters({ provider: "daily" });
          setAppliedFilters({ provider: "daily" });
          setPage(1);
          setHealthPage(1);
        }}
      />
      {activeQuery.isLoading ? (
        <p role="status" className="text-sm text-muted-foreground">
          {tab === "impact" ? "Loading Switchboard calls…" : "Loading Daily health…"}
        </p>
      ) : null}
      {activeQuery.error ? (
        <div
          role="alert"
          className="rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700"
        >
          {activeQuery.error.message}
        </div>
      ) : null}
      {tab === "health" && healthQuery.data ? (
        <div role="tabpanel" aria-labelledby="switchboard-tab-health">
          <SwitchboardDailyHealth
            data={healthQuery.data}
            onPage={setHealthPage}
            fetching={healthQuery.isFetching}
          />
        </div>
      ) : null}
      {tab === "impact" && data ? (
        <div role="tabpanel" aria-labelledby="switchboard-tab-impact" className="space-y-5">
          <KpiGrid className="md:grid-cols-3 lg:grid-cols-6">
            <KpiCard
              label="Call attempts"
              value={data.summary.totalCalls.toLocaleString()}
              tooltip="All claimed dialing attempts matching the applied filters, including preparation failures and in-progress calls. Undialed queue entries are excluded."
            />
            <KpiCard
              label="Finished attempts"
              value={data.summary.completedCalls.toLocaleString()}
              tooltip="Attempts that reached a final outcome, including failures."
            />
            <KpiCard
              label="Transferred"
              value={data.summary.transferredCalls.toLocaleString()}
              tooltip="A confirmed navigator handoff in the native call record."
            />
            <KpiCard
              label="Voicemail systems"
              value={data.summary.voicemailSystems.toLocaleString()}
              footnote={`${data.summary.voicemailCalls.toLocaleString()} complete messages recorded`}
            />
            <KpiCard
              label="Failed / transfer failed"
              value={data.summary.failedCalls.toLocaleString()}
            />
            <KpiCard
              label="Activity linked"
              value={data.summary.callsWithActivity.toLocaleString()}
              footnote={`${data.summary.totalCalls ? Math.round((data.summary.callsWithActivity / data.summary.totalCalls) * 100) : 0}% of attempts`}
            />
          </KpiGrid>
          <SwitchboardPathReview data={data} />
          <SwitchboardCallTable data={data} onPage={setPage} fetching={query.isFetching} />
        </div>
      ) : null}
    </DashboardsPageFrame>
  );
}
